// TicketCategoryList.js - Ticket Category Management with CRUD

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, IconButton, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Chip, Tooltip, Stack, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { ticketCategoryService } from './helpDeskService';
import { TicketPriorityOptions, TicketPriorityLabels, TicketPriorityColors } from './helpDeskEnums';

const TicketCategoryList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // Dialog states
  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parent_id: null,
    default_priority: '',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    document.title = 'Ticket Categories | Help Desk';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadCategories();
    }
  }, [canView, chatbotId]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    setLoading(true);
    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      const response = await ticketCategoryService.getAll(params);
      if (response.success) {
        // Flatten for display but keep hierarchy info
        const flatList = [];
        (response.data || []).forEach(cat => {
          flatList.push({ ...cat, level: 0 });
          if (cat.subcategories) {
            cat.subcategories.forEach(sub => {
              flatList.push({ ...sub, level: 1, parentName: cat.name });
            });
          }
        });
        setCategories(flatList);
      }
    } catch (err) {
      enqueueSnackbar('Failed to load categories', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (category = null) => {
    if (category) {
      setSelectedCategory(category);
      setFormData({
        name: category.name || '',
        description: category.description || '',
        parent_id: category.parent_id || null,
        default_priority: category.default_priority || '',
        display_order: category.display_order || 0,
        is_active: category.is_active !== false
      });
    } else {
      setSelectedCategory(null);
      setFormData({
        name: '',
        description: '',
        parent_id: null,
        default_priority: '',
        display_order: 0,
        is_active: true
      });
    }
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedCategory(null);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      enqueueSnackbar('Category name is required', { variant: 'error' });
      return;
    }

    setFormLoading(true);
    try {
      const data = {
        ...formData,
        chatbot_id: chatbotId,
        default_priority: formData.default_priority || null
      };

      let response;
      if (selectedCategory) {
        response = await ticketCategoryService.update(selectedCategory.id, data);
      } else {
        response = await ticketCategoryService.create(data);
      }

      if (response.success) {
        enqueueSnackbar(
          selectedCategory ? 'Category updated successfully' : 'Category created successfully',
          { variant: 'success' }
        );
        handleCloseForm();
        loadCategories();
      } else {
        enqueueSnackbar(response.message || 'Failed to save category', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to save category', { variant: 'error' });
      console.error(err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleOpenDeleteDialog = (category) => {
    setSelectedCategory(category);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedCategory(null);
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;

    setFormLoading(true);
    try {
      const response = await ticketCategoryService.delete(selectedCategory.id);
      if (response.success) {
        enqueueSnackbar('Category deleted successfully', { variant: 'success' });
        handleCloseDeleteDialog();
        loadCategories();
      } else {
        enqueueSnackbar(response.message || 'Failed to delete category', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to delete category', { variant: 'error' });
      console.error(err);
    } finally {
      setFormLoading(false);
    }
  };

  // Get parent categories for dropdown (only top-level)
  const parentCategories = categories.filter(c => c.level === 0);

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view this page.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/helpdesk`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Ticket Categories
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage categories for organizing support tickets
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={loadCategories} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          {canModify && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
            >
              Add Category
            </Button>
          )}
        </Box>
      </Box>

      {/* Categories Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Default Priority</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Status</TableCell>
                {canModify && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canModify ? 6 : 5} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canModify ? 6 : 5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No categories found</Typography>
                    {canModify && (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenForm()}
                        sx={{ mt: 2 }}
                      >
                        Create First Category
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category) => (
                  <TableRow key={category.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {category.level > 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
                            └
                          </Typography>
                        )}
                        <Typography variant="body2" fontWeight={category.level === 0 ? 600 : 400}>
                          {category.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 250 }}>
                        {category.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {category.default_priority ? (
                        <Chip
                          size="small"
                          label={TicketPriorityLabels[category.default_priority]}
                          color={TicketPriorityColors[category.default_priority]}
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{category.display_order}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={category.is_active ? 'Active' : 'Inactive'}
                        color={category.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    {canModify && (
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenForm(category)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDeleteDialog(category)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedCategory ? 'Edit Category' : 'Create New Category'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Category Name"
              required
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              placeholder="e.g., Technical Support, Billing"
            />

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder="Brief description of this category"
            />

            <FormControl fullWidth>
              <InputLabel>Parent Category</InputLabel>
              <Select
                value={formData.parent_id || ''}
                label="Parent Category"
                onChange={(e) => handleFormChange('parent_id', e.target.value || null)}
              >
                <MenuItem value="">None (Top Level)</MenuItem>
                {parentCategories
                  .filter(c => c.id !== selectedCategory?.id)
                  .map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Default Priority</InputLabel>
              <Select
                value={formData.default_priority}
                label="Default Priority"
                onChange={(e) => handleFormChange('default_priority', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {TicketPriorityOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label="Display Order"
              value={formData.display_order}
              onChange={(e) => handleFormChange('display_order', parseInt(e.target.value) || 0)}
              helperText="Lower numbers appear first"
            />

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.is_active ? 'active' : 'inactive'}
                label="Status"
                onChange={(e) => handleFormChange('is_active', e.target.value === 'active')}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm} disabled={formLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={formLoading}
            startIcon={formLoading && <CircularProgress size={20} />}
          >
            {selectedCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the category "{selectedCategory?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. If tickets are using this category, deletion may fail.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={formLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={formLoading}
            startIcon={formLoading && <CircularProgress size={20} />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TicketCategoryList;
